import { useCallback, useState } from 'react';
import { getClarityProjectId, injectClarity } from '@/features/consent/utils/clarity';

const STORAGE_KEY = 'banquinha_analytics_consent';

type ConsentStatus = 'pending' | 'accepted' | 'declined';

interface ConsentRecord {
  accepted: boolean;
  timestamp: string;
}

function readConsent(): ConsentStatus {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 'pending';
    const record: ConsentRecord = JSON.parse(raw);
    return record.accepted ? 'accepted' : 'declined';
  } catch {
    return 'pending';
  }
}

function tryInjectClarity(): void {
  const projectId = getClarityProjectId();
  if (projectId) {
    injectClarity(projectId);
  }
}

export function useAnalyticsConsent() {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>(() => {
    const status = readConsent();
    if (status === 'accepted') {
      tryInjectClarity();
    }
    return status;
  });

  const acceptConsent = useCallback(() => {
    const record: ConsentRecord = {
      accepted: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    setConsentStatus('accepted');
    tryInjectClarity();
  }, []);

  const declineConsent = useCallback(() => {
    const record: ConsentRecord = {
      accepted: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    setConsentStatus('declined');
  }, []);

  const resetConsent = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setConsentStatus('pending');
  }, []);

  return {
    consentStatus,
    acceptConsent,
    declineConsent,
    resetConsent,
  };
}
